import { Box, Skeleton } from "@mantine/core"
import { ArrowDownIcon, ArrowUpIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { IDashboard } from "../../../models/Dashboard"
import {
  formatChange,
  formatCount,
  formatCurrencyShort,
  STAT_CONFIGS,
  StatConfig,
} from "../utils"
import classes from "../real.module.css"
import { KpiTile } from "./KpiTile"

interface Props {
  amounts: IDashboard["amounts"] | undefined
  isBuyer: boolean
}

export function KpiRow({ amounts, isBuyer }: Props) {
  return (
    <Box className={classes.kpiRow}>
      {amounts
        ? amounts.map((amount, i) => (
            <KpiCard key={i} amount={amount} config={STAT_CONFIGS[i] ?? STAT_CONFIGS[0]} isBuyer={isBuyer} />
          ))
        : [1, 2, 3, 4].map((i) => <Skeleton key={i} h={122} radius="md" />)}
    </Box>
  )
}

/** @dehub/help `field` id per KPI label key. */
const KPI_HELP: Record<string, string> = {
  "stats.salesOrder": "kpi-sales",
  "stats.orderCount": "kpi-count",
  "stats.orderItems": "kpi-items",
  "stats.averageOrder": "kpi-average",
}

interface KpiCardProps {
  amount: NonNullable<IDashboard["amounts"]>[number]
  config: StatConfig
  isBuyer: boolean
}

function KpiCard({ amount, config, isBuyer }: KpiCardProps) {
  const { t } = useTranslation(["dashboard"])
  const change = formatChange(amount.changePct)
  const DiffIcon = change.isPositive ? ArrowUpIcon : ArrowDownIcon

  const currency = formatCurrencyShort(amount.amount || 0)

  let changeText: string
  if (change.isNew || change.isUnknown) changeText = change.label
  else if (change.isFlat) changeText = t("dashboard:samePrevMonth")
  else changeText = `${change.label} ${t("dashboard:prevMonth")}`

  return (
    <KpiTile
      icon={config.icon}
      label={t(`dashboard:${isBuyer && config.buyerLabelKey ? config.buyerLabelKey : config.labelKey}`)}
      helpKey={KPI_HELP[config.labelKey] ? `order.dashboard.field.${KPI_HELP[config.labelKey]}` : undefined}
      value={config.isCurrency ? currency.num : formatCount(amount.amount || 0)}
      unit={config.isCurrency ? currency.unit : t("dashboard:numberFormat.unit", { defaultValue: "ш" })}
      foot={
        <>
          {!change.isNew && !change.isUnknown && !change.isFlat && <DiffIcon size={14} weight="bold" />}
          {changeText}
        </>
      }
    />
  )
}
